import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, LoadingSpinner } from '@tadagram/ui';
import { apiService } from '../../services/api';
import { ExternalLink, Trash2, RefreshCw, Users, ChevronLeft, ChevronRight, Download, X, Plus } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface JoinedGroup {
  id: string;
  business_id: string;
  account_id: string;
  list_name: string;
  group_name: string;
  group_url: string;
  status: 'joined' | 'pending_approval';
  joined_at: string;
  updated_at: string;
}

interface GroupsResponse {
  groups: JoinedGroup[];
  total: number;
  page: number;
  limit: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function shortLabel(label: string, max = 22): string {
  if (!label) return 'default';
  return label.length > max ? label.slice(0, max) + '…' : label;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation('leads');
  if (status === 'joined') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
        Đã tham gia
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
      {t('statusPending')}
    </span>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export const JoinedGroupsDashboard: React.FC = () => {
  const { t } = useTranslation('leads');
  const [groups, setGroups] = useState<JoinedGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [listNames, setListNames] = useState<string[]>([]);
  const [filterAccountId, setFilterAccountId] = useState('');
  const [filterListName, setFilterListName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // ── Manual create states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ group_url: '', group_name: '', account_id: '', list_name: 'manual', status: 'joined' });

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchGroups = useCallback(
    async (
      targetPage = 1,
      accountId = filterAccountId,
      listName = filterListName,
      status = filterStatus,
    ) => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {
          page: String(targetPage),
          limit: String(limit),
        };
        if (accountId) params.account_id = accountId;
        if (listName) params.list_name = listName;
        if (status) params.status = status;

        const qs = new URLSearchParams(params).toString();
        const res = await apiService.get<{ success: boolean; data: GroupsResponse }>(
          `/api/v1/joined-groups?${qs}`,
        );
        const data: GroupsResponse = res?.data ?? { groups: [], total: 0, page: 1, limit };
        setGroups(data.groups ?? []);
        setTotal(data.total ?? 0);
        setPage(data.page ?? 1);

        // Build filter dropdowns from all-account fetch only
        if (!accountId && !listName) {
          const seenAccounts = new Set<string>();
          const seenLists = new Set<string>();
          (data.groups ?? []).forEach((g) => {
            if (g.account_id) seenAccounts.add(g.account_id);
            if (g.list_name) seenLists.add(g.list_name);
          });
          setAccountIds((prev) => Array.from(new Set([...prev, ...seenAccounts])).sort());
          setListNames((prev) => Array.from(new Set([...prev, ...seenLists])).sort());
        }
      } catch (err: any) {
        setError(err?.message ?? 'Không tải được dữ liệu');
      } finally {
        setLoading(false);
      }
    },
    [filterAccountId, filterListName, filterStatus, limit],
  );

  useEffect(() => {
    fetchGroups(1, '', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = (v: string) => {
    setFilterAccountId(v);
    setFilterListName('');
    fetchGroups(1, v, '', filterStatus);
  };

  const handleListNameChange = (v: string) => {
    setFilterListName(v);
    fetchGroups(1, filterAccountId, v, filterStatus);
  };

  const handleStatusChange = (v: string) => {
    setFilterStatus(v);
    fetchGroups(1, filterAccountId, filterListName, v);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchGroups(newPage, filterAccountId, filterListName, filterStatus);
  };

  // ── Manual create
  const handleManualCreate = async () => {
    if (!addForm.group_url.trim()) {
      setAddError('Vui lòng nhập URL nhóm');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await apiService.post('/api/v1/joined-groups', {
        group_url: addForm.group_url,
        group_name: addForm.group_name || undefined,
        account_id: addForm.account_id || undefined,
        list_name: addForm.list_name || 'manual',
        status: addForm.status || 'joined',
      });
      setShowAddModal(false);
      setAddForm({ group_url: '', group_name: '', account_id: '', list_name: 'manual', status: 'joined' });
      fetchGroups();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      if (e?.response?.status === 409) {
        setAddError('Nhóm này đã tồn tại trong hệ thống');
      } else {
        setAddError(e?.response?.data?.message || 'Lỗi khi thêm, thử lại sau');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (group: JoinedGroup) => {
    if (!window.confirm(t('deleteGroupConfirm', { name: group.group_name || group.group_url }))) return;
    setDeletingId(group.id);
    try {
      await apiService.delete(`/api/v1/joined-groups/${group.id}`);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      alert(t('deleteFailed', { error: err?.message ?? '' }));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Export CSV ───────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = { page: '1', limit: '10000' };
      if (filterAccountId) params.account_id = filterAccountId;
      if (filterListName) params.list_name = filterListName;
      if (filterStatus) params.status = filterStatus;
      const qs = new URLSearchParams(params).toString();
      const res = await apiService.get<{ success: boolean; data: GroupsResponse }>(
        `/api/v1/joined-groups?${qs}`,
      );
      const rows = res?.data?.groups ?? [];
      if (rows.length === 0) {
        alert(t('noDataToExport'));
        return;
      }

      const esc = (val: string | number) => {
        const s = String(val ?? '');
        const escaped = s.replace(/"/g, '""');
        return /[,"\n\r]/.test(s) ? `"${escaped}"` : escaped;
      };

      const headers = ['Tài khoản', 'Danh sách', 'Tên nhóm', 'Link nhóm', 'Trạng thái', 'Tham gia lúc', 'Cập nhật'];
      const csvRows = rows.map((g) => [
        esc(g.account_id),
        esc(g.list_name),
        esc(g.group_name),
        esc(g.group_url),
        esc(g.status === 'joined' ? 'Đã tham gia' : 'Chờ duyệt'),
        esc(formatDate(g.joined_at)),
        esc(formatDate(g.updated_at)),
      ]);

      const csv = '\uFEFF' + [headers.join(','), ...csvRows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      const accPart = filterAccountId ? filterAccountId.slice(0, 8) : 'all';
      link.download = `joined_groups_${accPart}_${filterListName || 'all'}_${dateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(t('exportFailed', { error: err?.message ?? '' }));
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-500/10 text-blue-500 shrink-0">
            <Users size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">{t('joinedGroupsTitle')}</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              {t('joinedGroupsSubtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleExport}
            disabled={exporting || loading || groups.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            <Download size={14} className={exporting ? 'animate-bounce' : ''} />
            {exporting ? t('exporting') : t('exportExcel')}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shrink-0"
          >
            <Plus size={14} />
            Thêm thủ công
          </button>
          <button
            onClick={() => fetchGroups(1, filterAccountId, filterListName, filterStatus)}
            disabled={loading}
            className="p-2 rounded-lg text-text-secondary hover:bg-bg-secondary hover:text-text-primary transition-colors disabled:opacity-40 shrink-0"
            title={t('refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-secondary whitespace-nowrap font-medium">{t('accountFilter')}</label>
          <select
            className="input text-sm py-1.5 w-auto min-w-[170px]"
            value={filterAccountId}
            onChange={(e) => handleAccountChange(e.target.value)}
          >
            <option value="">{t('all')}</option>
            {accountIds.map((aid) => (
              <option key={aid} value={aid}>{aid}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-secondary whitespace-nowrap font-medium">{t('listFilter')}</label>
          <select
            className="input text-sm py-1.5 w-auto min-w-[160px]"
            value={filterListName}
            onChange={(e) => handleListNameChange(e.target.value)}
          >
            <option value="">{t('all')}</option>
            {listNames.map((ln) => (
              <option key={ln} value={ln}>{ln}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-secondary whitespace-nowrap font-medium">{t('statusFilter')}</label>
          <select
            className="input text-sm py-1.5 w-auto min-w-[140px]"
            value={filterStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="">{t('all')}</option>
            <option value="joined">{t('statusJoined')}</option>
            <option value="pending_approval">{t('statusPending')}</option>
          </select>
        </div>
        <span className="text-xs text-text-tertiary ml-auto">
          {t('totalGroups', { count: total })}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-tg-danger/10 border border-tg-danger/30 text-tg-danger rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" text={t('loading')} />
        </div>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center">
          <Users size={40} className="mx-auto mb-3 text-text-tertiary opacity-40" />
          <p className="text-sm text-text-secondary">{t('noData')}</p>
          <p className="text-xs text-text-tertiary mt-1">
            {t('workerGroupNote')}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-secondary">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-36">{t('colAccount')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colList')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">{t('colGroupName')}</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-10">{t('colLink')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-28">{t('colStatus')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colJoinedAt')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colUpdatedAt')}</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {groups.map((group) => (
                  <tr key={group.id} className="hover:bg-bg-secondary transition-colors duration-100">
                    {/* Account */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleAccountChange(filterAccountId === group.account_id ? '' : group.account_id)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono transition-colors max-w-[130px] truncate ${
                          filterAccountId === group.account_id
                            ? 'bg-tg-primary text-white'
                            : 'bg-bg-secondary text-text-secondary hover:bg-tg-primary/10 hover:text-tg-primary'
                        }`}
                        title={t('filterByAccount', { id: group.account_id })}
                      >
                        {group.account_id}
                      </button>
                    </td>

                    {/* List name */}
                    <td className="px-4 py-3">
                      <span
                        className="badge badge-info inline-block max-w-[130px] truncate align-middle"
                        title={group.list_name || 'default'}
                      >
                        {shortLabel(group.list_name)}
                      </span>
                    </td>

                    {/* Group name */}
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-medium text-text-primary block max-w-xs truncate"
                        title={group.group_name || group.group_url}
                      >
                        {group.group_name || <span className="text-text-tertiary italic">{t('noGroupName')}</span>}
                      </span>
                    </td>

                    {/* Group link */}
                    <td className="px-4 py-3 text-center">
                      {group.group_url ? (
                        <a
                          href={group.group_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-tg-primary/10 text-tg-primary hover:bg-tg-primary/20 transition-colors"
                          title={group.group_url}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge status={group.status} />
                    </td>

                    {/* Joined at */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-text-secondary">{formatDate(group.joined_at)}</span>
                    </td>

                    {/* Updated at */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-text-secondary">{formatDate(group.updated_at)}</span>
                    </td>

                    {/* Delete */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(group)}
                        disabled={deletingId === group.id}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-text-tertiary hover:bg-tg-danger/10 hover:text-tg-danger transition-colors disabled:opacity-40"
                        title="Xóa"
                      >
                        {deletingId === group.id ? (
                          <RefreshCw size={13} className="animate-spin" />
                        ) : (
                          <Trash2 size={13} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-bg-secondary">
              <span className="text-xs text-text-tertiary">
                Trang {page}/{totalPages} — {total} nhóm
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || loading}
                  className="p-1.5 rounded-lg text-text-secondary hover:bg-bg-primary hover:text-text-primary transition-colors disabled:opacity-40"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="p-1.5 rounded-lg text-text-secondary hover:bg-bg-primary hover:text-text-primary transition-colors disabled:opacity-40"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Manual Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-bg-primary rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-text-primary">Thêm nhóm thủ công</h3>
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="p-1.5 rounded-lg text-text-secondary hover:bg-bg-secondary transition-colors"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              {addError && <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">{addError}</div>}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">URL nhóm <span className="text-red-500">*</span></label>
                <input type="url" value={addForm.group_url} onChange={e => setAddForm(f => ({ ...f, group_url: e.target.value }))} placeholder="https://facebook.com/groups/..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Tên nhóm</label>
                <input type="text" value={addForm.group_name} onChange={e => setAddForm(f => ({ ...f, group_name: e.target.value }))} placeholder="Tên nhóm Facebook..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Account ID</label>
                <input type="text" value={addForm.account_id} onChange={e => setAddForm(f => ({ ...f, account_id: e.target.value }))} placeholder="Để trống = manual" className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Danh sách</label>
                <input type="text" value={addForm.list_name} onChange={e => setAddForm(f => ({ ...f, list_name: e.target.value }))} placeholder="manual" className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Trạng thái</label>
                <select value={addForm.status} onChange={e => setAddForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500">
                  <option value="joined">Đã tham gia</option>
                  <option value="pending_approval">Chờ duyệt</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="px-4 py-2 text-sm text-text-secondary hover:bg-bg-secondary rounded-lg transition-colors">Hủy</button>
              <button onClick={handleManualCreate} disabled={addLoading} className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50">{addLoading ? 'Đang lưu...' : 'Thêm nhóm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
